import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthService } from '../services/AuthService';
import {
  getSavedLoginId,
  setSavedLoginId,
  isAutoLoginEnabled,
  setAutoLogin,
} from '../utils/auth';
import LuxroboMark from '../components/branding/LuxroboMark';
import styles from './LoginPage.module.css';

/**
 * Login / 메인 진입 화면.
 *
 * 외부 사람에게 노출되는 첫 surface라서, "운영 시스템 + LUXROBO 브랜드" 이
 * 한눈에 전달되는 split layout 으로 구성.
 *
 *   좌  brand panel  : 다크 네이비 + sage olive accent. 운영 정체성.
 *   우  form panel   : 화이트 surface. 입력 가독성.
 *
 * 좁은 화면(1024px↓)에서는 좌 panel 이 숨고 form 위쪽에 mini brand header 만
 * 남는다 — CSS 측에서 처리, 컴포넌트 분기는 단일 트리 유지.
 *
 * 로그인은 username(로그인 아이디)으로 — 이메일과는 별개 필드.
 */

const SYSTEM_NAME = '스마트 주차 관리 운영 플랫폼';
const SYSTEM_LEDE = '현장의 주차 데이터를 한곳에서 관제하고, 운영 절차를 자동화합니다.';
const FEATURES = ['현장 데이터 관리', '실시간 관제', '운영 자동화', '권한 분리'];
const VERSION_LABEL = 'BUILD 2026.05';

// 우측 form panel 은 디자인상 항상 화이트 surface — 그러나 앱 글로벌 테마가
// dark 모드면 MUI TextField 가 흰 글자/흰 배경으로 그려져 입력이 안 보인다.
// 페이지 안에서만 입력 색을 명시적으로 박아 테마 모드와 무관하게 항상 가독성 확보.
// autofill (Chrome) 의 노란 배경/흰 글자도 동일하게 흡수.
const loginInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    color: '#0f172a',
    '& fieldset': { borderColor: 'rgba(15, 23, 42, 0.16)' },
    '&:hover fieldset': { borderColor: 'rgba(15, 23, 42, 0.32)' },
    '&.Mui-focused fieldset': { borderColor: '#0b1320', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { color: '#475569' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0b1320' },
  '& .MuiOutlinedInput-input': {
    color: '#0f172a',
    caretColor: '#0f172a',
    '&::placeholder': { color: '#94a3b8', opacity: 1 },
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 100px #ffffff inset',
      WebkitTextFillColor: '#0f172a',
      caretColor: '#0f172a',
    },
  },
} as const;

/**
 * 백엔드/네트워크/axios 가 던지는 다양한 형태의 에러에서 사용자에게 보여줄 한국어
 * 메시지를 뽑아낸다.
 *
 * - 백엔드 4xx body: AuthService.login 의 catch 가 `error.response.data` 를 그대로 throw
 *   → 보통 `{success:false, message:"..."}` plain object. `instanceof Error` false.
 * - 네트워크/axios 자체 에러: `instanceof Error` true. message 에 'Network Error' 등.
 * - 그 외: generic fallback.
 *
 * 백엔드의 raw message 가 사용자에게 보이기에 기술적이면(`Validation failed: ...`,
 * `invalid credentials` 등) 친화 문구로 매핑한다.
 */
function extractLoginErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && !(err instanceof Error)) {
    const body = err as { message?: string; error?: string };
    const raw = (body.message ?? body.error ?? '').toString().trim();
    if (raw) {
      if (
        /validation failed/i.test(raw) ||
        /invalid (id|email|password|credentials|login)/i.test(raw) ||
        /password|비밀번호/i.test(raw) ||
        /unauthor/i.test(raw)
      ) {
        return '아이디 또는 비밀번호를 확인해 주세요.';
      }
      if (/not found|존재하지 않/i.test(raw)) {
        return '등록되지 않은 계정입니다. 운영팀에 문의해 주세요.';
      }
      return raw;
    }
  }
  if (err instanceof Error) {
    if (/network/i.test(err.message)) return '네트워크 연결을 확인해 주세요.';
    if (/timeout/i.test(err.message)) return '응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.';
    if (/40[13]|unauthor/i.test(err.message)) return '아이디 또는 비밀번호를 확인해 주세요.';
    return err.message || '로그인 중 오류가 발생했습니다.';
  }
  return '로그인 중 오류가 발생했습니다.';
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const savedId = getSavedLoginId();
  const [username, setUsername] = useState(savedId ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberId, setRememberId] = useState(Boolean(savedId));
  const [autoLogin, setAutoLoginChecked] = useState(isAutoLoginEnabled());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 아이디 저장 / 자동 로그인 설정 — 로그인 시도 전에 반영해야
      // AuthService.login 내부 setTokens 가 자동 로그인 여부를 보고 저장 위치를 결정한다.
      setSavedLoginId(rememberId ? username : null);
      setAutoLogin(autoLogin);

      const response = await AuthService.login({ username, password });

      if (response.success) {
        navigate('/projects');
      } else {
        setError(response.message || '로그인에 실패했습니다.');
      }
    } catch (err: unknown) {
      setError(extractLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={styles.page}>
      {/* ──────────────────────────  좌 — Brand panel  ────────────────────────── */}
      <aside className={styles.brandPanel} aria-label="LUXROBO 브랜드">
        <header className={styles.brandHeader}>
          <span className={styles.brandMark} aria-hidden="true">
            <LuxroboMark size={36} />
          </span>
          <span className={styles.brandWordmark}>
            LUX<span>ROBO</span>
          </span>
        </header>

        <div className={styles.brandBody}>
          <span className={styles.brandKicker}>SMART PARKING OPERATIONS</span>
          <h1 className={styles.brandHeadline}>
            현장의 모든 데이터를
            <strong>한 화면에서.</strong>
          </h1>
          <p className={styles.brandLede}>{SYSTEM_LEDE}</p>
          <ul className={styles.brandFeatures}>
            {FEATURES.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </div>

        <footer className={styles.brandFooter}>
          <span className={styles.brandStatus}>운영 환경 정상</span>
          <span>{VERSION_LABEL}</span>
        </footer>
      </aside>

      {/* ──────────────────────────  우 — Form panel  ────────────────────────── */}
      <section className={styles.formPanel} aria-labelledby="login-heading">
        <div className={styles.formMobileBrand} aria-hidden="true">
          <span className={styles.brandMark}>
            <LuxroboMark size={28} />
          </span>
          <span className={styles.brandWordmark}>
            LUX<span>ROBO</span>
          </span>
        </div>

        <div className={styles.formInner}>
          <h2 id="login-heading" className={styles.formHeading}>
            로그인
          </h2>
          <p className={styles.formSub}>
            {SYSTEM_NAME} 에 접속하려면 사내 계정으로 로그인하세요.
          </p>

          {error && (
            <div className={styles.errorBanner} role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <fieldset className={styles.fieldset} disabled={loading}>
              <TextField
                fullWidth
                id="username"
                label="아이디"
                name="username"
                autoComplete="username"
                autoFocus
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                size="medium"
                variant="outlined"
                error={Boolean(error)}
                sx={loginInputSx}
              />

              <TextField
                fullWidth
                id="password"
                name="password"
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                size="medium"
                variant="outlined"
                error={Boolean(error)}
                sx={loginInputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                        onClick={() => setShowPassword((v) => !v)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        size="small"
                        // 입력 배경을 항상 화이트로 고정(loginInputSx)하므로, 다크 테마의
                        // 기본 아이콘 색(흰색)이 그대로 상속되면 배경에 묻혀 안 보인다.
                        sx={{ color: '#475569', '&:hover': { color: '#0f172a' } }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <div className={styles.optionsRow}>
                <label className={styles.optionLabel}>
                  <input
                    type="checkbox"
                    checked={rememberId}
                    onChange={(e) => setRememberId(e.target.checked)}
                  />
                  아이디 저장
                </label>
                <label className={styles.optionLabel}>
                  <input
                    type="checkbox"
                    checked={autoLogin}
                    onChange={(e) => setAutoLoginChecked(e.target.checked)}
                  />
                  자동 로그인
                </label>
              </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading}
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <span className={styles.submitSpinner} aria-hidden="true" />
                    로그인 중...
                  </>
                ) : (
                  '로그인'
                )}
              </button>
            </fieldset>
          </form>

          <div className={styles.formFooter}>
            <span>© LUXROBO {new Date().getFullYear()}</span>
            <span>문의: 운영팀</span>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
